import { useMutation, useQuery, useSubscription } from "./urqlHooks";
import moment from "moment";

const GET_NOTIFICATIONS = `
  query GET_NOTIFICATIONS ($userId: String! $companyId: String) {
    getNotifications(userId: $userId, companyId: $companyId)
  }
`;

const GET_APP_NOTIFICATIONS_LIST = `
  query GET_APP_NOTIFICATIONS_LIST($userId: String! $companyId: String) {
    appNotifications(userId: $userId, companyId: $companyId) {
      id
      content
      createdAt
      redirectUrl
      referenceType
      botname
      clientName
      documentFolio
      recipients {
        user
        isRead
      }
      company {
        alias
        id
        name
        plan
        rut
        suscriptionId
        trialExpirationDate
      }
      reference {
        ... on Document {
          meta {
            ... on TaxDocumentMeta {
              documentId
            }
            ... on ObligationMeta {
              documentId
            }
          }
        }
      }
      sender {
        id
        profile {
          firstName
          lastName
        }
        emails {
          address
        }
      }
    }
  }
`;

export const GetNotifications = (userId, companyId = null) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_NOTIFICATIONS,
    variables: { userId, companyId: companyId || null },
    pause: !userId,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

export const GetAppNotificationsList = (
  userId,
  companyId = null,
  { pause = false, requestPolicy = "network-only" } = {}
) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_APP_NOTIFICATIONS_LIST,
    variables: { userId, companyId: companyId || null },
    pause: !userId || pause,
    requestPolicy,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const APP_NOTIFICATIONS = `subscription APP_NOTIFICATIONS ($userId: String!) {
    notificationsReceived(userId: $userId) {
      id
      content
      createdAt
      redirectUrl
      referenceType
      botname
      clientName
      documentFolio
      recipients {
        user
        isRead
      }
      company {
        alias
        id
        name
        plan
        rut
        suscriptionId
        trialExpirationDate
      }
      reference {
        ... on Document {
          meta  {
            ... on TaxDocumentMeta {
              documentId
            }
            ... on ObligationMeta {
              documentId
            }
          }
        }
      }
      sender {
        id
        profile {
          firstName
          lastName
        }
        emails {
          address
        }
      }
    }
}`;

const isPlaceholderProfileName = (value) => {
  const trimmed = String(value ?? "").trim();
  return !trimmed || trimmed === "-";
};

/** Etiqueta del remitente en la campana: prioriza correo sobre nombre/apellido. */
export const getNotificationSenderLabel = (sender) => {
  if (!sender) return "Sistema";

  const emailFromList = (sender.emails || []).find((e) =>
    String(e?.address || "").includes("@")
  )?.address;
  if (emailFromList) return String(emailFromList).trim();

  const fullName = [sender?.profile?.firstName, sender?.profile?.lastName]
    .filter((part) => !isPlaceholderProfileName(part))
    .join(" ")
    .trim();
  if (fullName) return fullName;

  return "Sistema";
};

/** Botlogs de conexión/conciliación automática: toast vía WS, no campana. */
export const BELL_HIDDEN_BOTLOG_BOTNAMES = new Set([
  'conciliacion-automatica',
  'conciliacion-automatica-llm',
  'conciliacion-automatica-cron',
]);

const shouldHideBellNotification = (notification) => {
  const referenceTypeLower = String(notification?.referenceType || '').toLowerCase();
  if (referenceTypeLower === 'platform_connection') return true;
  const botname = String(notification?.botname || '').toLowerCase();
  return BELL_HIDDEN_BOTLOG_BOTNAMES.has(botname);
};

export const formatAppNotifications = (rawNotifications = []) => {
  const formattedNotifications = [];
  // Tipos visibles en la campana: reglas, cobranza y menciones en comentarios (documento / conciliación).
  const allowedReferenceTypes = new Set([
    'reconcile_rules_mining',
    'reconcile_rule_approved',
    'reconcile_rule_saved',
    'reconcile_rule_executed',
    'match_recommendations',
    'contact_account_suggestions',
    'cobranza_automatizacion',
    'cobranza_assignment',
    'document',
    'transaction',
    'repository',
    'cuadre_cartolas',
    'bitacora',
    'pago_proveedor',
    'cobranza',
    'haberes_variables',
  ]);

  for (const notification of rawNotifications) {
    if (!notification) continue;
    if (shouldHideBellNotification(notification)) continue;

    const referenceTypeLower = String(notification?.referenceType || '').toLowerCase();
    if (!allowedReferenceTypes.has(referenceTypeLower)) continue;
    const defaultRulesSuggestionsRedirectUrl = '/configuracion#zona-entrenamiento?tab=rules';
    const defaultMatchRedirectUrl = '/configuracion#zona-entrenamiento';
    
    // Determinar redirectUrl según tipo de notificación
    let redirectUrl = notification?.redirectUrl;
    if (!redirectUrl) {
      if (referenceTypeLower === 'reconcile_rules_mining') {
        redirectUrl = defaultRulesSuggestionsRedirectUrl;
      } else if (referenceTypeLower.startsWith('reconcile_rule')) {
        redirectUrl = '/configuracion#reglas-conciliacion';
      } else if (referenceTypeLower === 'match_recommendations') {
        redirectUrl = defaultMatchRedirectUrl;
      } else if (referenceTypeLower === 'contact_account_suggestions') {
        redirectUrl = `${typeof window !== 'undefined' ? window.location.pathname : '/dashboard'}#sugerencias-cuentas`;
      }
    }
    const common = {
      id: notification?.id,
      redirectUrl,
      comment: notification?.content || '',
      ago: moment(notification?.createdAt).format('DD-MM-YYYY HH:mm:ss'),
      notification,
      recipients: notification?.recipients,
      company: notification?.company,
      referenceType: notification?.referenceType,
      ok: undefined,
    };

    if (referenceTypeLower === 'reconcile_rules_mining') {
      formattedNotifications.push({
        ...common,
        title: 'Reglas sugeridas disponibles',
        description: `Empresa ${notification?.company?.alias || notification?.company?.name} - Revisa las reglas sugeridas en Reglas de Automatización`,
      });
      continue;
    }

    if (referenceTypeLower === 'reconcile_rule_approved') {
      formattedNotifications.push({
        ...common,
        title: 'Regla aprobada',
        description: notification?.content || `Empresa ${notification?.company?.alias || notification?.company?.name} - Se aprobó una regla sugerida`,
      });
      continue;
    }

    if (referenceTypeLower === 'match_recommendations') {
      formattedNotifications.push({
        ...common,
        title: 'Nuevas recomendaciones de match',
        description: notification?.content || `Empresa ${notification?.company?.alias || notification?.company?.name} - Revisa las sugerencias en Zona de Entrenamiento`,
      });
      continue;
    }

    if (referenceTypeLower === 'contact_account_suggestions') {
      formattedNotifications.push({
        ...common,
        title: 'Sugerencias de cuentas disponibles',
        description:
          notification?.content ||
          `Empresa ${notification?.company?.alias || notification?.company?.name} - Revisa las cuentas propuestas para clientes y proveedores`,
      });
      continue;
    }

    if (referenceTypeLower === 'reconcile_rule_saved') {
      formattedNotifications.push({
        ...common,
        title: 'Regla guardada',
        description: `Empresa ${notification?.company?.alias || notification?.company?.name} - Se guardó una regla de conciliación`,
      });
      continue;
    }

    if (referenceTypeLower === 'reconcile_rule_executed') {
      formattedNotifications.push({
        ...common,
        title: 'Regla ejecutada',
        description: `Empresa ${notification?.company?.alias || notification?.company?.name} - Se ejecutó una regla de conciliación`,
      });
      continue;
    }

    if (referenceTypeLower === 'cobranza_automatizacion') {
      formattedNotifications.push({
        ...common,
        title: 'Automatización de cobranza',
        description:
          notification?.content ||
          `Empresa ${notification?.company?.alias || notification?.company?.name} - Actualizaste el envío automático de plantillas de cobranza.`,
      });
      continue;
    }

    if (referenceTypeLower === 'pago_proveedor') {
      const senderLabel = getNotificationSenderLabel(notification?.sender);
      const companyLabel =
        notification?.company?.alias || notification?.company?.name || 'Empresa';
      formattedNotifications.push({
        ...common,
        redirectUrl: redirectUrl || '/pago_proveedores',
        senderLabel,
        titleVariant: 'mention',
        companyLabel,
        title: `${senderLabel} te mencionó en Pago a Proveedores`,
        description: `${companyLabel} · Nota interna`,
        contextLabel: 'Pago a proveedores',
      });
      continue;
    }

    if (referenceTypeLower === 'cobranza_assignment') {
      const companyLabel =
        notification?.company?.alias || notification?.company?.name || 'Empresa';
      const assignmentTitle =
        notification?.content ||
        'Fuiste asignado para la gestión de la cobranza de un cliente.';
      let cobranzaRedirectUrl = redirectUrl || '/cobranza?tab=gestion';
      try {
        const parsed = new URL(cobranzaRedirectUrl, 'https://portal.smartcfo.cl');
        if (!parsed.searchParams.get('tab')) {
          parsed.searchParams.set('tab', 'gestion');
        }
        cobranzaRedirectUrl = `${parsed.pathname}?${parsed.searchParams.toString()}`;
      } catch {
        cobranzaRedirectUrl = '/cobranza?tab=gestion';
      }
      formattedNotifications.push({
        ...common,
        redirectUrl: cobranzaRedirectUrl,
        title: assignmentTitle,
        description: `${companyLabel} · Cobranza`,
        contextLabel: 'Cobranza',
      });
      continue;
    }

    if (referenceTypeLower === 'cobranza') {
      const senderLabel = getNotificationSenderLabel(notification?.sender);
      const companyLabel =
        notification?.company?.alias || notification?.company?.name || 'Empresa';
      // "empresa" = deudor/cliente de la cobranza; folio de factura solo si el
      // comentario se dejó sobre un documento.
      const clientName = String(notification?.clientName || '').trim();
      const documentFolio = String(notification?.documentFolio || '').trim();
      const contextLabel = ['Cobranza']
        .concat(clientName ? [clientName] : [])
        .concat(documentFolio ? [`Factura #${documentFolio}`] : [])
        .join(' · ');
      const description = [companyLabel]
        .concat(clientName ? [clientName] : [])
        .concat(documentFolio ? [`Factura #${documentFolio}`] : ['Nota interna'])
        .join(' · ');
      formattedNotifications.push({
        ...common,
        redirectUrl: redirectUrl || '/cobranza',
        senderLabel,
        titleVariant: 'mention',
        companyLabel,
        title: `${senderLabel} te mencionó en Cobranza`,
        description,
        contextLabel,
      });
      continue;
    }

    const senderLabel = getNotificationSenderLabel(notification?.sender);
    const companyLabel =
      notification?.company?.alias || notification?.company?.name || "Empresa";
    const contextLabel =
      referenceTypeLower === "document"
        ? notification?.reference?.meta?.documentId
          ? `Documentos · folio #${notification?.reference?.meta?.documentId}`
          : "Documentos tributarios"
        : referenceTypeLower === "repository"
          ? "Repositorio"
          : referenceTypeLower === "cuadre_cartolas"
            ? "Cuadre de Cartolas"
            : referenceTypeLower === "bitacora"
              ? "Bitácora · Flujo de caja"
              : referenceTypeLower === "haberes_variables"
                ? "Haberes Variables"
                : "Conciliación bancaria";

    formattedNotifications.push({
      senderLabel,
      companyLabel,
      contextLabel,
      title: `${senderLabel} te ha mencionado en un comentario`,
      description: `${companyLabel} · ${contextLabel}`,
      comment: notification?.content || "",
      ago: moment(notification?.createdAt).format("DD-MM-YYYY HH:mm:ss"),
      id: notification?.id,
      redirectUrl: notification?.redirectUrl,
      notification: notification,
      recipients: notification?.recipients,
      company: notification?.company,
      ok: undefined,
    });
  }

  return formattedNotifications;
};

export const AppNotificationsSuscription = (userId, { pause = false } = {}) => {
  const [res] = useSubscription({
    query: APP_NOTIFICATIONS,
    variables: { userId: String(userId || '') },
    pause: pause || !userId,
  });

  return res;
};

// Crear una notificación persistida (para mostrar en la campana)
// Nota: requiere soporte en backend.
export const CREATE_APP_NOTIFICATION = `
  mutation CreateAppNotification($input: CreateAppNotificationInput!) {
    createAppNotification(input: $input) {
      id
      content
      redirectUrl
      referenceType
      createdAt
      recipients {
        user
        isRead
      }
      company {
        id
        name
      }
      sender {
        id
        profile {
          firstName
          lastName
        }
        emails {
          address
        }
      }
    }
  }
`;

export const CreateAppNotification = () => {
  const [result, executeMutation] = useMutation(CREATE_APP_NOTIFICATION);
  const { data, fetching, error } = result;

  const sanitizeCreateAppNotificationInput = (input) => {
    if (!input || typeof input !== 'object') return input;
    const referenceTypeLower = String(input?.referenceType || '').toLowerCase();

    // Only allow `isBot` to be set from the frontend for rule-related notifications.
    // Everything else must ignore it to avoid changing legacy notification behavior.
    const allowIsBot =
      referenceTypeLower === 'reconcile_rules_mining' ||
      referenceTypeLower.startsWith('reconcile_rule');

    if (allowIsBot) return input;

    // Strip `isBot` for non-rule notifications.
    // eslint-disable-next-line no-unused-vars
    const { isBot, ...rest } = input;
    return rest;
  };

  const executeMutationSafe = (variables, context) => {
    const nextVariables = {
      ...(variables || {}),
      input: sanitizeCreateAppNotificationInput(variables?.input),
    };
    return executeMutation(nextVariables, context);
  };

  return { data, fetching, error, executeMutation: executeMutationSafe };
};

export const MARK_NOTIFICATION_AS_READ = `
  mutation MARK_NOTIFICATION_AS_READ(
    $notificationId: String!
    $userId: String!
    $companyId: String
  ) {
    markNotificationAsRead(notificationId: $notificationId, userId: $userId, companyId: $companyId)
  }
`;

export const MarkNotificationAsReaded = () => {
  const [result, executeMutation] = useMutation(MARK_NOTIFICATION_AS_READ);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

export const MARK_ALL_NOTIFICATION_AS_READ = `
  mutation MARK_ALL_NOTIFICATION_AS_READ {
    markAllNotificationAsRead
  }
`;

export const MarkAllNotificationAsRead = () => {
  const [result, executeMutation] = useMutation(MARK_ALL_NOTIFICATION_AS_READ);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};